import { Controller } from "@hotwired/stimulus"
import { findScrollParent, scrollIntoView } from "../concern/scroll_into_view"

const HIGHLIGHT_CLASS = ["outline", "outline-[3px]", "outline-offset-[-3px]", "outline-purple-500", "relative", "z-10"]

// Connects to data-controller="video-timecodes"
export default class extends Controller {
  static outlets = ["video-player"]
  static targets = ["row", "stickyTop", "anchor"]
  static values = {
    currentTime: Number,
  }

  initialize() {
    this.visibleElements = new Set()
    this.isScrolling = false
  }

  connect() {
    this.#setObserver()
  }

  disconnect() {
    this.visibleElements.forEach((elem) => {
      this.#removeHighlight(elem)
    })
    this.visibleElements.clear()
    this.observer.disconnect()
  }

  rowTargetConnected(target) {
    this.observer.observe(target)
  }

  rowTargetDisconnected(target) {
    this.observer.unobserve(target)
  }

  seekTo(event) {
    const time = Number.parseFloat(event.params.time)

    if (Number.isNaN(time)) {
      console.warn("Invalid start time", event.target)
      return
    }
    const bitFuture = time + 0.01

    this.videoPlayerOutlet.seekTo(bitFuture)
  }

  scrollTo(timecode) {
    if (this.isScrolling) return

    const target = this.#findTarget(timecode)
    if (!target) {
      this.scrollToFallback(timecode)
      return
    }

    this.#addHighlight(target)
    this.#scrollToTarget(target, {
      jumpThreshold: 2000,
      offsetTop: this.#getStickTopSize(),
      offsetBottom: 20,
    })
  }

  scrollToFallback(timecode) {
    // if (this.isScrolling) return

    const target = this.#findAnchorTarget(timecode)
    if (!target) {
      console.warn("No target found for the timecode", timecode)
      return
    }

    this.#scrollToTarget(target, { jumpThreshold: 2000 })
  }

  updateCurrentTime(time) {
    this.currentTimeValue = time
  }

  currentTimeValueChanged() {
    this.visibleElements.forEach((elem) => {
      this.#updateHighlight(elem)
    })
  }

  #findTarget(timecode) {
    return this.rowTargets.find((row) => this.#isTimeInRange(timecode, row))
  }

  #findAnchorTarget(timecode) {
    return this.anchorTargets.find((anchor) => this.#isTimeInRange(timecode, anchor))
  }

  #isTimeInRange(time, element) {
    const startTime = Number.parseFloat(element.dataset.timeCodeStart)
    const endTime = Number.parseFloat(element.dataset.timeCodeEnd)
    return time >= startTime && time < endTime
  }

  #scrollToTarget(target, options) {
    const scrollElem = findScrollParent(target)
    if (!scrollElem) {
      console.warn("No scrollable parent found for the target", target)
      return
    }

    this.isScrolling = true

    if (options.jumpThreshold) {
      scrollIntoView(target, {
        ...options,
        scrollTimeout: 150,
      }).then(() => {
        this.isScrolling = false
      })
    } else {
      target.scrollIntoView({ behavior: "smooth", ...options })
      this.isScrolling = false
    }
  }

  #getStickTopSize() {
    return Array.prototype.reduce.call(this.stickyTopTargets || [], (acc, node) => acc + node.clientHeight, 0)
  }

  #setObserver() {
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.visibleElements.add(entry.target)
          this.#updateHighlight(entry.target)
        } else {
          this.visibleElements.delete(entry.target)
          this.#updateHighlight(entry.target)
        }
      })
    })

    this.rowTargets.forEach((row) => {
      this.observer.observe(row)
    })
  }

  #updateHighlight(elem) {
    const startTime = Number.parseFloat(elem.dataset.timeCodeStart)
    const endTime = Number.parseFloat(elem.dataset.timeCodeEnd)
    if (Number.isNaN(startTime) || Number.isNaN(endTime)) {
      console.warn("Invalid start/end time", elem)
      return
    }

    const shouldHighlight = this.currentTimeValue >= startTime && this.currentTimeValue < endTime

    if (shouldHighlight) {
      this.#addHighlight(elem)
    } else {
      this.#removeHighlight(elem)
    }
  }

  #addHighlight(elem) {
    elem.classList.add(...HIGHLIGHT_CLASS)
  }

  #removeHighlight(elem) {
    elem.classList.remove(...HIGHLIGHT_CLASS)
  }
}
